/**
 * Firebase Configuration
 * Initializes Firebase app for authentication
 */

import { initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app'
import {
  getAuth,
  type Auth,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  onAuthStateChanged,
  type User as FirebaseUser
} from 'firebase/auth'

/**
 * Get Firebase configuration from environment variables
 *
 * @returns Firebase configuration object
 * @throws Error if required environment variables are missing
 */
export function getFirebaseConfig(): FirebaseOptions {
  const config: FirebaseOptions = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: import.meta.env.VITE_FIREBASE_APP_ID,
  }

  // Optional: Analytics measurement ID
  if (import.meta.env.VITE_FIREBASE_MEASUREMENT_ID) {
    config.measurementId = import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
  }

  // Validate required fields
  const requiredFields: (keyof FirebaseOptions)[] = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
  ]

  for (const field of requiredFields) {
    if (!config[field]) {
      throw new Error(
        `Missing required Firebase config: VITE_FIREBASE_${field.toUpperCase()}`
      )
    }
  }

  return config
}

// Initialize Firebase app (singleton)
let firebaseApp: FirebaseApp | null = null
let firebaseAuth: Auth | null = null

/**
 * Initialize Firebase app
 * Only initializes once (singleton pattern)
 *
 * @returns Firebase app instance
 */
export function initializeFirebase(): FirebaseApp {
  if (!firebaseApp) {
    const config = getFirebaseConfig()
    firebaseApp = initializeApp(config)
  }
  return firebaseApp
}

/**
 * Get Firebase Auth instance
 * Initializes Firebase if not already initialized
 *
 * @returns Firebase Auth instance
 */
export function getFirebaseAuth(): Auth {
  if (!firebaseAuth) {
    const app = initializeFirebase()
    firebaseAuth = getAuth(app)
  }
  return firebaseAuth
}

/**
 * Export auth instance for convenience
 * This is initialized lazily on first access
 */
export const auth = getFirebaseAuth()

/**
 * Set Firebase authentication persistence
 *
 * @param rememberMe - If true, uses local persistence (survives browser close), otherwise session persistence (expires on tab close)
 */
export async function setAuthPersistence(rememberMe: boolean = true): Promise<void> {
  const auth = getFirebaseAuth()
  const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence
  await setPersistence(auth, persistence)
}

/**
 * Setup Firebase auth state listener
 * Call this once when the app initializes
 *
 * @param callback - Function to call when auth state changes
 * @returns Unsubscribe function
 */
export function onAuthStateChange(callback: (user: FirebaseUser | null) => void): () => void {
  const auth = getFirebaseAuth()
  return onAuthStateChanged(auth, callback)
}